import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { TenantCatalogProduct } from '../types/models/TenantCatalogProduct';
import type { ApiResponseTenantCatalog } from '../types/api/ApiResponseTenantCatalog';

interface UseTenantCatalogReturn {
  products: TenantCatalogProduct[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /prices/products — the read-only customer catalog: every ACTIVE and tenant-VISIBLE product
// (hidden-but-billable rows, `tenant_visible = false` — e.g. per-deal Special rebate products — are
// filtered out server-side, migration 00074) read straight from the catalog tables (not the
// PriceableProduct registry), each with its category embedded and the in-scope client's effective
// price, ordered by category (id = display order) then product label. Resolves for
// the in-scope client (a clientless caller gets platform defaults); useApiFetch injects the bearer
// token and the impersonation header, so the list reflects the impersonated client with no extra work
// here. Re-runs when the in-scope client changes (the apiFetch identity flips on impersonation/token
// renew). These are reads with no editable form state, so a renew-triggered refetch clobbers nothing.
export const useTenantCatalog = (enabled = true): UseTenantCatalogReturn => {
  const [products, setProducts] = useState<TenantCatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/prices/products');
      // Branch on the JSend `body.status`, NEVER `response.ok`: the backend maps controller
      // exceptions to `status: 'error'` at HTTP 200, so `response.ok` can be true on a failure.
      const body = (await response.json().catch(() => null)) as ApiResponseTenantCatalog | null;
      if (body?.status === 'success' && body.data?.products) {
        setProducts(body.data.products);
      } else {
        setError(body?.message ?? 'Failed to load the product catalog');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the catalog');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchProducts();
    }
  }, [enabled, auth.isAuthenticated, fetchProducts]);

  return { products, loading, error, refetch: fetchProducts };
};
